import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { adhocService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { TicketView } from '@unpispas/upp-data';

/** Payload when Checkout opens — host holds tickets and opens WAITER; settle is ticket state. */
export type TicketPayActiveChange = {
    active: boolean;
    /** True so the host opens the ONLINE wait panel. */
    waiting: boolean;
    tickets: TicketView[];
};

/********************************/
/* TICKET / GUEST CHECKOUT      */
/********************************/

/**
 * Stripe pay button for guest tickets already committed (`objid`).
 *
 * Only opens Checkout: server owns `PAYMENT` + ticket `PP`/`PAYLINE`. The host
 * reacts to ticket model updates (`PP` → wait, `PD` → paid, released → retry).
 */
@Component({
    selector: 'upp-payment-ticket',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-ticket.html',
    styleUrls: ['./upp-ticket.scss'],
})
export class UppPaymentTicketComponent extends languageSupport {
    @Input() tickets: TicketView[] | null = null;
    /** Sum shown in UI (required for anti-tamper). */
    @Input() amount: number | null = null;
    @Input() disabled = false;

    /** Fired once Checkout URL is ready (host opens wait / keeps this widget mounted). */
    @Output() ActiveChange = new EventEmitter<TicketPayActiveChange>();

    private _payInFlight = false;

    constructor(
        lang: languageService,
        change: ChangeDetectorRef,
        private toast: toastService,
        private adhoc: adhocService,
        private route: ActivatedRoute,
    ) {
        super(lang, change);
    }

    get CanPay(): boolean {
        return !this.disabled
            && !this._payInFlight
            && !!this.tickets
            && this.tickets.length > 0
            && this.amount != null
            && Number.isFinite(this.amount);
    }

    /**
     * POST checkout and open Stripe. Durable write is server-side; guest must not
     * `DoPayment` / flush. Host settles from ticket sync after {@link ActiveChange}.
     */
    async OnPay(): Promise<void> {
        if (!this.CanPay || !this.tickets || this.amount == null) {
            return;
        }

        const _payTickets = [...this.tickets];
        const _amount = this.amount;
        this._payInFlight = true;
        this._chng?.markForCheck();
        this.toast.ShowWait();

        // Open during the click gesture so the browser allows navigation after await.
        const _checkout = window.open('about:blank', '_blank');
        let _navigated = false;
        try {
            const _ids: string[] = [];
            for (const _ticket of _payTickets) {
                const _objid = _ticket.object?.objid ?? _ticket.objid;
                if (!_objid) {
                    throw new Error(
                        '[PAYMENT] Ticket has not been committed (not ready for checkout)'
                    );
                }

                _ids.push(String(_objid));
            }

            const params: Record<string, string | number> = {
                tickets: _ids.join(','),
                amount: _amount,
            };

            const _validQr = this.route.snapshot.queryParamMap.get('validQR');
            if (_validQr) {
                params['validQR'] = _validQr;
            }

            const _table = this.route.snapshot.queryParamMap.get('table');
            if (_table) {
                params['table'] = _table;
            }

            const data: any = await this.adhoc.DoRequest(
                'guest/payment/checkout',
                params,
                {},
                true
            );

            if (data['errorcode'] == 0 && data['url']) {
                const _url = String(data['url']);

                if (_checkout) {
                    _checkout.opener = null;
                    _checkout.location.href = _url;
                    _checkout.focus();
                }
                else {
                    window.open(_url, '_blank', 'noopener,noreferrer');
                }

                _navigated = true;
                this.ActiveChange.emit({
                    active: true,
                    waiting: true,
                    tickets: _payTickets,
                });
                return;
            }

            if (data['errorcode'] != null && data['errorcode'] !== 0) {
                this.toast.ShowAlert('danger', this.tr('@service_request_error_' + data['errorcode']));
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            this._payInFlight = false;
            this.toast.HideWait();
            this._chng?.markForCheck();

            if (!_navigated) {
                _checkout?.close();
            }
        }
    }
}
